import ApiUtils from '@shared/utils/api';
import Content from '../types/enums/content';
import EpgEvent from './epgEvent';
import ImageUtils from '../utils/image';
import IStreams from '../types/interfaces/streams';
import MathUtils from '../utils/math';
import Plan from './plan';
import Services from '../types/enums/services';
import StreamType from '../types/enums/stream';
import StreamUtils from '../utils/stream';
import TranslationManager from '@shared/assets/locale/translationsManager';
import { store } from '../store/store';
import { EventMap } from '@shared/types/interfaces/event';
import {
  IChannelAdvertisement,
  IChannelCdnBalancer,
  IChannelRequest,
} from '../types/interfaces/channel';

class Channel {
  advertisement?: IChannelAdvertisement;
  allowedPlans: string[];
  avplay?: boolean;
  background: string;
  card: string;
  category: string;
  cdnBalancer: IChannelCdnBalancer;
  code: string;
  currentEvent?: EpgEvent | null;
  deadman: boolean;
  dial: number;
  enableZappingUpgrade: boolean;
  extraPlans: Plan[];
  fake: {
    eventName: string;
    longDescription: string;
    shortDescription: string;
  };
  favorite: boolean;
  hidden: boolean;
  id: string;
  name: string;
  isFast: boolean;
  logo: string;
  logoWhite: string;
  hqLogoWhite: string;
  parentalControl: boolean;
  plans: string[];
  postal?: {
    province?: string[];
    scope?: string;
  };
  poster: string;
  p2p: boolean;
  services: string[];
  sortIndex?: number;
  streams: IStreams;
  title: string;
  type: string;
  resolution: string;
  upgradablePlans: Plan[];

  constructor(requestChannel: IChannelRequest) {
    this.advertisement = requestChannel?.master?.advertisement;
    this.allowedPlans = requestChannel?.plans || [];
    this.avplay = requestChannel?.master?.AVPlay ?? false;
    this.background = requestChannel?.master?.genericBackgroundImage;
    this.card = requestChannel?.master?.genericCardImage;
    this.category = requestChannel?.master?.category;
    this.cdnBalancer = requestChannel?.master?.cdnBalancer;
    this.code = requestChannel?.channelCode || '';
    this.currentEvent = null;
    this.deadman = requestChannel?.master?.deadman ?? true;
    this.dial = requestChannel?.dial;
    this.fake = {
      eventName:
        requestChannel?.master?.genericEventName ||
        TranslationManager.get('MISC.FAKE.EVENT_NAME'),
      longDescription: requestChannel?.master?.longDescription,
      shortDescription: requestChannel?.master?.shortDescription,
    };
    this.favorite = requestChannel?.favorite || false;
    this.hidden = requestChannel?.hidden;
    this.enableZappingUpgrade =
      requestChannel?.master?.enableZappingPlanUpgrade;
    this.extraPlans =
      (requestChannel?.extraPlans
        ?.map((item) => {
          return Plan.extraPlan(item);
        })
        .filter((item) => !!item) as Plan[]) || [];
    this.id =
      requestChannel?.master?.id || requestChannel?.id || requestChannel?._id;
    this.name = requestChannel?.master?.name || requestChannel?.name;
    this.isFast = !!requestChannel?.master?.isFast;
    this.logo = ImageUtils.getImage(requestChannel?.master?.logoColor, 'logo');
    this.hqLogoWhite = ImageUtils.getImage(
      requestChannel?.master?.logoBlanco,
      'hqLogo'
    );
    this.logoWhite = ImageUtils.getImage(
      requestChannel?.master?.logoBlanco,
      'logo'
    );
    this.parentalControl = requestChannel?.master?.parentalControl;
    this.plans = requestChannel?.plans || [];
    this.postal = {
      province: requestChannel?.master?.postalProvincia,
      scope: requestChannel?.master?.scope,
    };
    this.poster = requestChannel?.master?.genericPosterImage;
    this.p2p = requestChannel?.master?.p2p;
    this.resolution = requestChannel?.master?.quality;
    this.services = requestChannel?.master?.services;
    this.streams = {
      dash: StreamUtils.checkStreamType(
        requestChannel?.master?.streaming?.DASH,
        StreamType.DASH
      ),
      free: requestChannel?.master?.streaming?.FREE_STREAM,
      hls: StreamUtils.checkStreamType(
        requestChannel?.master?.streaming?.HLS,
        StreamType.HLS
      ),
      subtitles: requestChannel?.master?.streaming?.SUBTITLES_STREAM,
    };
    this.title = requestChannel?.master?.title;
    this.type =
      requestChannel?.master?.type === 'AVOD'
        ? Content.AVOD
        : requestChannel?.master?.type === 'SVOD'
        ? Content.SVOD
        : Content.CHANNEL;
    this.upgradablePlans =
      requestChannel?.upgradablePlans?.map((item) => {
        return Plan.upgradablePlan(item);
      }) || [];
    if (this.logo && this.logo !== 'undefined' && !this.logo.includes('http')) {
      this.logo = ApiUtils.getUrl('baseImgUrl') + this.logo;
    }
    if (
      this.logoWhite &&
      this.logoWhite !== 'undefined' &&
      !this.logoWhite.includes('http')
    ) {
      this.logoWhite = ApiUtils.getUrl('baseImgUrl') + this.logoWhite;
    }
  }

  getCurrentEvent() {
    if (this.currentEvent) return this.currentEvent;
    else {
      const { events } = store.getState();
      try {
        return (this.currentEvent = events.currentEvents[this.id]);
      } catch (e) {
        return null;
      }
    }
  }

  getMockedEvent() {
    const { events } = store.getState();
    try {
      return (this.currentEvent = events.mockedEvents[this.id]);
    } catch (e) {
      return null;
    }
  }

  getEventByIndex(index?: number, data?: EventMap) {
    try {
      if (!data) {
        const { events } = store.getState();
        data = events.eventsData;
      }
      const parsedIndex = MathUtils.numberWithinRange(
        index || 0,
        0,
        data[this.id].length - 1
      );
      return data[this.id][parsedIndex];
    } catch (e) {
      return null;
    }
  }

  getEventsRange(data?: EventMap) {
    if (!data) {
      const { events } = store.getState();
      data = events.eventsData;
    }
    return {
      firstIndex: 0,
      currentIndex:
        this.currentEvent && data[this.id]?.length
          ? Math.max(data[this.id]?.indexOf(this.currentEvent), 0)
          : 0,
      lastIndex: data[this.id]?.length ? data[this.id].length - 1 : 0,
    };
  }

  hasYouMissedIt() {
    return (
      this.services?.includes(Services.YOU_MISSED_IT) ||
      this.services?.includes(Services.YOU_MISSED_IT_FREE)
    );
  }
}

export default Channel;
